import React, { useState } from 'react';
import { KeyRound, LogOut } from 'lucide-react';
import { termsApi } from '../services/termsService';
import { getPasswordRuleError } from '../utils/passwordValidation';
import { Button } from './ui/button';

interface ForcePasswordChangeModalProps {
  /** Called after POST /api/auth/change-password succeeds. */
  onPasswordChanged: () => void;
  /** Logs the user out via the app's existing logout flow. */
  onLogout: () => void;
}

const PASSWORD_CHANGE_ERROR_MESSAGE =
  'We could not update your password. Please try again.';
const PASSWORDS_DO_NOT_MATCH_MESSAGE = 'Passwords do not match.';

/**
 * Blocking, non-dismissable gate shown when the backend flags
 * termsStatus.mustChangePassword — e.g. after an admin issued a temporary
 * password. Mirrors the backend rule client-side: at least 8 characters with
 * at least one letter and one number.
 */
const ForcePasswordChangeModal: React.FC<ForcePasswordChangeModalProps> = ({
  onPasswordChanged,
  onLogout,
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setApiError(null);

    const ruleError = getPasswordRuleError(newPassword);
    if (ruleError) {
      setValidationError(ruleError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setValidationError(PASSWORDS_DO_NOT_MATCH_MESSAGE);
      return;
    }
    setValidationError(null);

    setIsSubmitting(true);
    try {
      const response = await termsApi.changePassword(newPassword);
      if (response?.success) {
        onPasswordChanged();
      } else {
        setApiError(response?.message || PASSWORD_CHANGE_ERROR_MESSAGE);
      }
    } catch (error: any) {
      setApiError(error?.message || PASSWORD_CHANGE_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inlineError = validationError || apiError;

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="force-password-modal-title"
    >
      <div className="modal-panel max-w-md w-full">
        {/* Header */}
        <div className="border-b border-hairline p-6">
          <div className="flex items-center gap-3">
            <KeyRound className="w-6 h-6 text-signature" aria-hidden="true" />
            <div>
              <h2
                id="force-password-modal-title"
                className="heading-3"
              >
                Set your new password
              </h2>
              <p className="text-sm text-ink-muted mt-1">
                For your security, you must set a new password before
                continuing.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="p-6 space-y-4">
            <div>
              <label
                htmlFor="new-password"
                className="block text-sm font-medium text-ink-secondary mb-1"
              >
                New password
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => {
                  setNewPassword(event.target.value);
                  setValidationError(null);
                }}
                className="w-full px-3 py-2 border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-signature focus:border-signature text-sm"
              />
              <p className="text-xs text-ink-muted mt-1">
                At least 8 characters, with at least one letter and one number.
              </p>
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium text-ink-secondary mb-1"
              >
                Confirm new password
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  setValidationError(null);
                }}
                className="w-full px-3 py-2 border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-signature focus:border-signature text-sm"
              />
            </div>

            {inlineError && (
              <p className="text-sm text-error" role="alert">
                {inlineError}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-hairline p-4 bg-surface-subtle rounded-b-lg flex items-center justify-between gap-4">
            <Button type="button" variant="tertiary" onClick={onLogout} className="gap-2">
              <LogOut className="w-4 h-4" aria-hidden="true" />
              Log out
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save new password'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForcePasswordChangeModal;
